/* eslint-disable */
import { useGetLng } from "@saleor/components/Shop/query";
import { RotaryErrorFragment } from "@saleor/fragments/types/RotaryErrorFragment";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import { getStringOrPlaceholder, joinDateTime } from "@saleor/misc";
import RotaryItemDeleteDialog from "@saleor/rotary/components/RotaryItemDeleteDialog";
import RotaryItemEditDialog, {
  RotaryItemEditDialogFormData
} from "@saleor/rotary/components/RotaryItemEditDialog";
import RotaryPage, {
  RotaryPageFormData
} from "@saleor/rotary/components/RotaryPage/RotaryPage";
import { useRotaryCreateMutation } from "@saleor/rotary/mutations";
import {
  rotaryAddUrl,
  RotaryAddUrlDialog,
  RotaryAddUrlQueryParams,
  rotaryListPath,
  rotaryUrl
} from "@saleor/rotary/urls";
import { ReorderEvent } from "@saleor/types";
import { RotaryErrorCode } from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import {
  add,
  isSelected,
  move,
  remove,
  updateAtIndex
} from "@saleor/utils/lists";
import React from "react";
import { useIntl } from "react-intl";
interface RotaryDetailsProps {
  params: RotaryAddUrlQueryParams;
}

function areValuesEqual(
  a: RotaryItemEditDialogFormData,
  b: RotaryItemEditDialogFormData
) {
  return a.text === b.text;
}

const rotaryItemAlreadyExistsError: RotaryErrorFragment = {
  __typename: "RotaryError",
  code: RotaryErrorCode.ALREADY_EXISTS,
  field: "text"
};

const rotaryItemNotNullError: RotaryErrorFragment = {
  __typename: "RotaryError",
  code: RotaryErrorCode.NOT_FOUND,
  field: "text"
};

const rotaryItemProbabilityError: RotaryErrorFragment = {
  __typename: "RotaryError",
  code: RotaryErrorCode.UNIQUE,
  field: "probability"
};

const RotaryDetails: React.FC<RotaryDetailsProps> = ({ params }) => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const intl = useIntl();

  const [values, setValues] = React.useState<RotaryItemEditDialogFormData[]>(
    []
  );

  const [textChange, setTextChange] = React.useState<boolean>();
  const [clickItemID, setClickItemID] = React.useState<any>();

  const [valueErrors, setValueErrors] = React.useState<RotaryErrorFragment[]>(
    []
  );
  const [rotaryCreate, rotaryCreateOpts] = useRotaryCreateMutation({
    onCompleted: data => {
      if (data.rotaryCreate.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Successfully created rotary"
          })
        });
        navigate(rotaryUrl(data.rotaryCreate.rotary.id));
      }
    }
  });
  const id = params.id ? parseInt(params.id, 0) : undefined;

  const [openModal, closeModal] = createDialogActionHandlers<
    RotaryAddUrlDialog,
    RotaryAddUrlQueryParams
  >(navigate, rotaryAddUrl, params);

  React.useEffect(() => setValueErrors([]), [params.action]);
  const handleValueDelete = () => {
    setValues(remove(values[params.id], values, areValuesEqual));
    closeModal();
  };

  const pagevalues = values.map((item, itemIndex) => ({
    __typename: "RotaryItems" as "RotaryItems",
    id: itemIndex + "",
    probability: item.probability,
    text: item.text,
    voucher: item.voucher
  }));

  const handleItemUpdate = (input: RotaryItemEditDialogFormData) => {
    if (isSelected(input, values, areValuesEqual) && textChange) {
      setValueErrors([rotaryItemAlreadyExistsError]);
      return;
    }
    if (input.text === "") {
      if (input.voucher === null) {
        setValueErrors([rotaryItemNotNullError]);
        return;
      } else {
        input.text = input.voucher.name;
      }
    }
    if (values.length) {
      let num: number = 0;
      let valueData = values.filter((it, i) => it && i !== Number(clickItemID));

      valueData?.filter(x => (num += Number(x.probability)));
      num + Number(input.probability) > 1
        ? setValueErrors([rotaryItemProbabilityError])
        : (setValues(updateAtIndex(input, values, id)), closeModal());
    } else {
      Number(input.probability) > 1
        ? setValueErrors([rotaryItemProbabilityError])
        : (setValues(updateAtIndex(input, values, id)), closeModal());
    }
    setTextChange(false);
  };

  const handleItemCreate = (input: RotaryItemEditDialogFormData) => {
    setTextChange(false);
    if (isSelected(input, values, areValuesEqual)) {
      setValueErrors([rotaryItemAlreadyExistsError]);
      return;
    }
    if (input.text === "") {
      if (input.voucher === null) {
        setValueErrors([rotaryItemNotNullError]);
        rotaryItemProbabilityError;
        return;
      } else {
        input.text = input.voucher.name;
      }
    }
    if (values.length) {
      let num: number = 0;
      values.filter(x => (num += Number(x.probability)));
      num + Number(input.probability) > 1
        ? setValueErrors([rotaryItemProbabilityError])
        : (setValues(add(input, values)), closeModal());
    } else {
      Number(input.probability) > 1
        ? setValueErrors([rotaryItemProbabilityError])
        : (setValues(add(input, values)), closeModal());
    }
  };

  const handleValueReorder = ({ newIndex, oldIndex }: ReorderEvent) =>
    setValues(move(values[oldIndex], values, areValuesEqual, newIndex));

  const handleCreate = async (data: RotaryPageFormData) => {
    const input = {
      ...data,
      startDate: joinDateTime(data.startDate, data.startTime),
      endDate: data.hasEndDateTime
        ? joinDateTime(data.endDate, data.endTime)
        : null,
      items: values.map(item => ({
        probability: item.probability,
        text: item.text,
        voucher: item?.voucher?.id || ""
      }))
    };
    delete input.endTime;
    delete input.startTime;
    delete input.hasEndDateTime;
    delete input.languageCode;
    !input.startDate && delete input.startDate;
    const result = await rotaryCreate({
      variables: {
        input
      }
    });
    return result.data.rotaryCreate?.rotary?.id || null;
  };
  const handleSubmit = handleCreate;
  const { data: lngData } = useGetLng({});
  const availableLngs = lngData?.shop?.languages?.map(i => ({
    id: i.code,
    name: i.language
  }));

  return (
    <>
      <RotaryPage
        rotary={null}
        disabled={rotaryCreateOpts.loading}
        errors={rotaryCreateOpts.data?.rotaryCreate.errors || []}
        onBack={() => navigate(rotaryListPath)}
        onDelete={undefined}
        onSubmit={handleSubmit}
        onItemsAdd={() => openModal("add-value")}
        rotaryUpdateLanguage={null}
        onValueDelete={id =>
          openModal("remove-value", {
            id
          })
        }
        availableLngs={availableLngs}
        onValueReorder={handleValueReorder}
        onValueUpdate={id => {
          setClickItemID(id);
          openModal("edit-value", {
            id
          });
        }}
        saveButtonBarState={rotaryCreateOpts.status}
        items={pagevalues}
      />
      <RotaryItemEditDialog
        rotaryItem={null}
        confirmButtonState="default"
        disabled={false}
        errors={valueErrors}
        open={params.action === "add-value"}
        onClose={closeModal}
        onSubmit={handleItemCreate}
        textChange={setTextChange}
      />
      {values.length > 0 && (
        <>
          <RotaryItemDeleteDialog
            rotaryName={undefined}
            open={params.action === "remove-value"}
            text={getStringOrPlaceholder(values[id]?.text)}
            confirmButtonState="default"
            onClose={closeModal}
            onConfirm={handleValueDelete}
          />
          <RotaryItemEditDialog
            rotaryItem={values[params.id]}
            confirmButtonState="default"
            disabled={false}
            errors={valueErrors}
            open={params.action === "edit-value"}
            onClose={closeModal}
            onSubmit={handleItemUpdate}
            textChange={setTextChange}
          />
        </>
      )}
    </>
  );
};
RotaryDetails.displayName = "RotaryDetails";

export default RotaryDetails;
